import { createContext, use, useEffect, useLayoutEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';
import type { LiveStatus, SensorStatus } from '@atnt/shared';
import type { SensorDto } from '../../api';
import { useNow } from './clock';
import { liveStore, type ConnectionState, type HistoryPoint, type LiveMessage, type LiveReading, type LiveStore } from './live-store';

/**
 * React side of the live data layer. Wrap a page (or the editor canvas) in `<LiveProvider tenantId>`; components
 * read values with `useSensorValue(id)` / `useSensorValues(ids)` — mounting subscribes, unmounting releases.
 */

export interface SensorReading {
  sensorId: string;
  value: number | null;
  /**
   * Display status: the server status, or `offline` when the value is null / older than `staleAfterMs`,
   * or `unknown` when nothing has been received (yet).
   */
  status: SensorStatus;
  /** raw status reported by the server */
  liveStatus: LiveStatus | null;
  /** ISO time of the measurement */
  ts: string | null;
  stale: boolean;
}

interface LiveContextValue {
  store: LiveStore;
  tenantId: string | null;
  staleAfterMs: number;
  meta: ReadonlyMap<string, SensorDto>;
}

/** Hourly sources (unified server) report every ~60 min → 3 h without a reading = stale (02-constructor §11.3). */
export const DEFAULT_STALE_AFTER_MS = 3 * 3600_000;
const EMPTY_META: ReadonlyMap<string, SensorDto> = new Map();

const LiveContext = createContext<LiveContextValue>({ store: liveStore, tenantId: null, staleAfterMs: DEFAULT_STALE_AFTER_MS, meta: EMPTY_META });

export interface LiveProviderProps {
  /** tenant **UUID** (the WebSocket protocol requires the id, not the slug) */
  tenantId: string;
  /** sensor registry (`useSensors(tenantId).data`): units, decimals and device codes used for formatting */
  sensors?: readonly SensorDto[];
  /** a reading older than this is shown as offline/stale */
  staleAfterMs?: number;
  /** override the tab-wide store (tests, previews) */
  store?: LiveStore;
  children: ReactNode;
}

export function LiveProvider({ tenantId, sensors, staleAfterMs = DEFAULT_STALE_AFTER_MS, store = liveStore, children }: LiveProviderProps) {
  const meta: ReadonlyMap<string, SensorDto> = sensors ? new Map(sensors.map((s) => [s.id, s])) : EMPTY_META;

  // the registry carries the hub's latest values — use them as a head start
  useEffect(() => {
    if (!sensors?.length) return;
    store.apply(
      sensors.flatMap((s) => (s.ts ? [{ indicatorId: s.id, value: s.value, ts: s.ts, status: s.status === 'unknown' ? ('offline' as const) : s.status }] : [])),
    );
  }, [store, sensors]);

  return <LiveContext value={{ store, tenantId, staleAfterMs, meta }}>{children}</LiveContext>;
}

export function useLiveContext() {
  return use(LiveContext);
}

export function toSensorReading(sensorId: string, r: LiveReading | undefined, now: number, staleAfterMs: number): SensorReading {
  if (!r) return { sensorId, value: null, status: 'unknown', liveStatus: null, ts: null, stale: false };
  const t = r.ts ? Date.parse(r.ts) : Number.NaN;
  const stale = Number.isFinite(t) && now - t > staleAfterMs;
  const status: SensorStatus = r.value == null || r.status === 'offline' || stale ? 'offline' : r.status;
  return { sensorId, value: r.value, status, liveStatus: r.status, ts: r.ts, stale };
}

/** Latest reading of one sensor (subscribes while mounted). `null`/`undefined` id → an `unknown` reading. */
export function useSensorValue(sensorId: string | null | undefined): SensorReading {
  const { store, tenantId, staleAfterMs } = use(LiveContext);
  const id = sensorId ?? '';
  useEffect(() => (tenantId && id ? store.retain(tenantId, [id]) : undefined), [store, tenantId, id]);
  const reading = useSyncExternalStore(
    (cb) => (id ? store.listen(id, cb) : () => undefined),
    () => (id ? store.get(id) : undefined),
    () => undefined,
  );
  const now = useNow(60_000);
  return toSensorReading(id, reading, now, staleAfterMs);
}

const uniqueKey = (ids: readonly (string | null | undefined)[]) => [...new Set(ids.filter((x): x is string => Boolean(x)))].join('|');

/** Latest readings of several sensors, keyed by id. */
export function useSensorValues(sensorIds: readonly (string | null | undefined)[]): ReadonlyMap<string, SensorReading> {
  const { store, tenantId, staleAfterMs } = use(LiveContext);
  const key = uniqueKey(sensorIds);
  useEffect(() => {
    const ids = key ? key.split('|') : [];
    return tenantId && ids.length ? store.retain(tenantId, ids) : undefined;
  }, [store, tenantId, key]);
  const ids = key ? key.split('|') : [];
  const list = useSyncExternalStore(
    (cb) => store.listenMany(ids, cb),
    () => store.getMany(ids),
    () => store.getMany(ids),
  );
  const now = useNow(60_000);
  const out = new Map<string, SensorReading>();
  ids.forEach((id, i) => out.set(id, toSensorReading(id, list[i], now, staleAfterMs)));
  return out;
}

/** Values received since the page opened (live trend), keyed by id. */
export function useSensorHistory(sensorIds: readonly (string | null | undefined)[]): ReadonlyMap<string, readonly HistoryPoint[]> {
  const { store, tenantId } = use(LiveContext);
  const key = uniqueKey(sensorIds);
  useEffect(() => {
    const ids = key ? key.split('|') : [];
    return tenantId && ids.length ? store.retain(tenantId, ids) : undefined;
  }, [store, tenantId, key]);
  const ids = key ? key.split('|') : [];
  const list = useSyncExternalStore(
    (cb) => store.listenMany(ids, cb),
    () => store.getHistoryMany(ids),
    () => store.getHistoryMany(ids),
  );
  const out = new Map<string, readonly HistoryPoint[]>();
  ids.forEach((id, i) => out.set(id, list[i] ?? []));
  return out;
}

/** Tenant of the surrounding `<LiveProvider>` (null outside one). */
export function useLiveTenantId(): string | null {
  return use(LiveContext).tenantId;
}

/** WebSocket state: `idle` | `connecting` | `open` | `reconnecting`. */
export function useLiveConnection(): ConnectionState {
  const { store } = use(LiveContext);
  return useSyncExternalStore(
    (cb) => store.onConnectionChange(cb),
    () => store.connection,
    () => 'idle' as const,
  );
}

/**
 * Raw server messages — including types the value cache ignores (e.g. a future `{ type: 'alarm' }`).
 * The handler may change between renders without re-subscribing.
 */
export function useLiveMessage(handler: (msg: LiveMessage) => void) {
  const { store } = use(LiveContext);
  const ref = useRef(handler);
  useLayoutEffect(() => {
    ref.current = handler;
  });
  useEffect(() => store.onMessage((msg) => ref.current(msg)), [store]);
}

/** Registry entry of a sensor (unit, decimals, device code), when the provider was given `sensors`. */
export function useSensorMeta(sensorId: string | null | undefined): SensorDto | undefined {
  const { meta } = use(LiveContext);
  return sensorId ? meta.get(sensorId) : undefined;
}

export function useSensorMetaMap(): ReadonlyMap<string, SensorDto> {
  return use(LiveContext).meta;
}
